import type {
  Application,
  ValueConfig_V,
  ValueFunction,
  IntegrationDescription,
  Team,
  Member,
  ValueFunction_DateAddOffset,
  OutputDescription,
} from "@smartsuite/automation-engine-api";
import { Timestamp } from "@smartsuite/automation-engine-api";
import { t } from "i18next";
import * as R from "remeda";
import type {
  ActionCandidate,
  AutomationCandidate,
  TriggerCandidate,
} from "../../types/candidate";
import { otherFormatter, smartsuiteFormatter } from "./trigger";
import { isSmartSuiteIntegration } from "../../utils/constants";
import { otherActionFormatter, smartsuiteActionFormatter } from "./action";
import type { ActionsFormatter } from "./action";
import { parseDaysOffset } from "../../utils/days";
import { plural } from "../../locales/i18n";
import { getOutputDescriptions } from "../../utils/conditions-coverter.hook";
import { formatDateByUserPreferences } from "../../utils/date";

/* eslint-disable  @typescript-eslint/no-explicit-any */
const nowOffsetToString = (offset: string): string | { [key: string]: any } => {
  const nextDaysRegex = /^P\d+D$/;
  const pastDaysRegex = /^-P\d+D$/;

  switch (offset) {
    case "P0D":
      return { key: "phrase-builder.today" };
    case "P1D":
      return { key: "phrase-builder.tomorrow" };
    case "-P1D":
      return { key: "phrase-builder.yesterday" };
    case "-P7D":
      return { key: "phrase-builder.week-ago" };
    case "P7D":
      return { key: "phrase-builder.week-from-now" };
    case "-P1M":
      return { key: "phrase-builder.month-ago" };
    case "P1M":
      return { key: "phrase-builder.month-from-now" };
    case "-P3M":
      return { key: "phrase-builder.three-months-ago" };
    case "P3M":
      return { key: "phrase-builder.three-months-from-now" };
    case "-P1Y":
      return { key: "phrase-builder.year-ago" };
    case "P1Y":
      return { key: "phrase-builder.year-from-now" };
  }

  if (R.isTruthy(offset.match(nextDaysRegex))) {
    const number = offset.substring(1, offset.length - 1);
    return { key: "phrase-builder.next_number_of_days", data: { number } };
  }
  if (R.isTruthy(offset.match(pastDaysRegex))) {
    const number = offset.substring(2, offset.length - 1);
    return { key: "phrase-builder.past_number_of_days", data: { number } };
  }

  return "";
};

/**
 * The workspace directory a phrase needs to name the member/team value
 * functions. Those are stored as an id alone (`memberName: {memberId}`), so
 * without the lists there is nothing to render -- callers that have no
 * directory keep the historic empty string rather than printing a raw id.
 */
export interface PhraseDirectory {
  members: Member[];
  teams: Team[];
}

/** Same display name the editor pills use: the member's name, their first
 * email when unnamed, and "Unknown User" once they leave the workspace
 * (MemberEditor/utils.ts). */
const memberDisplayName = (
  memberId: string,
  directory: PhraseDirectory | undefined,
): string => {
  if (directory === undefined) {
    return "";
  }
  const member = directory.members.find(
    (candidate) => candidate.memberId === memberId,
  );
  if (member === undefined) {
    return "Unknown User";
  }
  return R.isTruthy(member.name) ? member.name : (member.email[0] ?? "");
};

const teamDisplayName = (
  teamId: string,
  directory: PhraseDirectory | undefined,
): string => {
  if (directory === undefined) {
    return "";
  }
  const team = directory.teams.find((candidate) => candidate.teamId === teamId);
  return team?.name ?? "Unknown Team";
};

const dateAddOffsetToString = (
  dateAddOffset: ValueFunction_DateAddOffset,
  outputList?: OutputDescription[],
  directory?: PhraseDirectory,
  /* eslint-disable  @typescript-eslint/no-explicit-any */
): { [key: string]: any } | string[] => {
  if (dateAddOffset.value === undefined || outputList === undefined) {
    return [];
  }
  const innerValueString = valueConfigsVToDescriptionString(
    dateAddOffset.value.values,
    outputList,
    directory,
  );
  const offset = parseDaysOffset(dateAddOffset.offset) ?? 0;
  innerValueString.push(
    ` ${offset >= 0 ? "+" : ""}${offset} ${t(
      plural("date-editor.interval-suffix-text", offset),
    )}`,
  );
  return innerValueString;
};

export const valueConfigsVToDescriptionString = (
  values: ValueConfig_V[],
  outputList: OutputDescription[],
  directory?: PhraseDirectory,
  /* eslint-disable  @typescript-eslint/no-explicit-any */
): { [key: string]: any } | string[] => {
  return values
    .map((value, index) => {
      if (index === 0) {
        return valueConfigVToDescriptionString(value, outputList, directory);
      }
      return {
        key: "phrase-builder.comma",
        data: {
          data: valueConfigVToDescriptionString(value, outputList, directory),
        },
      };
    })
    .filter((value) => R.isTruthy(value));
};

const valueConfigVToDescriptionString = (
  value: ValueConfig_V,
  outputList: OutputDescription[],
  directory?: PhraseDirectory,
  /* eslint-disable  @typescript-eslint/no-explicit-any */
): string | { [key: string]: any } => {
  switch (value.value.oneofKind) {
    case "string":
      return value.value.string;
    case "fileUrl":
      return value.value.fileUrl;
    case "bool":
      // eslint-disable-next-line @typescript-eslint/restrict-template-expressions
      return `${value.value.bool}`;
    case "number":
      return `${value.value.number}`;
    case "date":
      return { type: "date", date: value.value.date };
    case "option":
      return value.value.option.label;
    case "reference": {
      const outputId = value.value.reference.outputId;
      const output = outputList.find((output) => output.outputId === outputId);
      if (R.isTruthy(output)) {
        return output.label;
      }
      return outputId;
    }
    case "function":
      return valueFunctionToValueDataV(
        value.value.function,
        outputList,
        directory,
      );
    case "file":
      return value.value.file.metadata?.filename ?? "";
    case "checklist":
      return value.value.checklist.itemsToAdd.length.toString();
    case "smartdoc":
      return "";
    case "status":
      return value.value.status.status;
    case "tag":
      return value.value.tag.label;
    case "keyValue": {
      const keys =
        value.value.keyValue.key?.values.map((item) =>
          valueConfigVToDescriptionString(item, outputList, directory),
        ) ?? [];
      const values =
        value.value.keyValue.value?.values.map((item) =>
          valueConfigVToDescriptionString(item, outputList, directory),
        ) ?? [];
      const x: string[] = keys.reduce((acc: string[], key, index) => {
        const k = typeof key === "string" ? key : Object.keys(key);
        const v =
          typeof values[index] === "string"
            ? values[index]
            : Object.keys(values[index] ?? {});
        const str = `[${k.toString()}]: ${v.toString()}`;
        acc.push(str);
        return acc;
      }, []);
      return x.join(", ");
    }
    default:
      return "";
  }
};

export const valueFunctionToValueDataV = (
  func: ValueFunction,
  outputList?: OutputDescription[],
  directory?: PhraseDirectory,
  /* eslint-disable  @typescript-eslint/no-explicit-any */
): string | { [key: string]: any } => {
  switch (func.function.oneofKind) {
    case "now":
      return nowOffsetToString(func.function.now.offset);
    case "nowWithTime":
      return nowOffsetToString(func.function.nowWithTime.offset);
    case "dateAddOffset":
      return dateAddOffsetToString(
        func.function.dateAddOffset,
        outputList,
        directory,
      );
    // A member/team pick is stored as an id-only function, so every one of
    // these fell through to "" and the phrase lost its value (STR-14493).
    // They all name the same thing the editor pill shows -- the phrase says
    // WHO was picked, not which of their attributes the engine will read.
    case "memberName":
      return memberDisplayName(func.function.memberName.memberId, directory);
    case "memberEmail":
      return memberDisplayName(func.function.memberEmail.memberId, directory);
    case "memberPhone":
      return memberDisplayName(func.function.memberPhone.memberId, directory);
    case "teamId":
      return teamDisplayName(func.function.teamId.teamId, directory);
    case "teamMemberIds":
      return teamDisplayName(func.function.teamMemberIds.teamId, directory);
    case "teamEmails":
      return teamDisplayName(func.function.teamEmails.teamId, directory);
    case "teamPhones":
      return teamDisplayName(func.function.teamPhones.teamId, directory);
    default:
      return "";
  }
};

/**
 * Translates one phrase node produced by the builders in this directory.
 *
 * The shape is a tree, not a flat key+params: `valueConfigsVToDescriptionString`
 * emits the 2nd and later values as nested `{key: "phrase-builder.comma",
 * data: {...}}` nodes, and a date value as `{type: "date", date}`. So a node's
 * `data` values have to be translated BEFORE they are interpolated -- calling
 * `t(node.key, node.data)` directly stringifies those nested nodes to
 * "[object Object]" (BUG-21273).
 */
/* eslint-disable  @typescript-eslint/no-explicit-any */
export const translatePhraseNode = (
  level: { [key: string]: any },
  timezone: string,
): string => {
  const levelData: any = {};
  if (level.type === "date") {
    return formatDateByUserPreferences(
      Timestamp.toDate(level.date),
      timezone,
      "DD MMM YYYY",
    );
  }
  if (Array.isArray(level)) {
    return level.map((l) => translatePhraseNode(l, timezone)).join("");
  }
  if (R.isTruthy(level.data)) {
    for (const data in level.data) {
      levelData[data] = translatePhraseNode(level.data[data], timezone);
    }
  }
  if (R.isTruthy(level.key)) {
    return t(level.key, { ...levelData });
  }
  if (typeof level === "string") {
    return level;
  }
  return "";
};

export const translateDescription = (
  descriptionJson: string,
  timezone: string,
): string => {
  try {
    return translatePhraseNode(JSON.parse(descriptionJson), timezone);
  } catch (e) {
    console.error(e);
    return "";
  }
};

export const generateDescription = (
  automation: AutomationCandidate,
  applications: Application[],
  integrations: IntegrationDescription[],
  members: Member[],
  teams: Team[],
): string => {
  const getTriggerText = (
    trigger: TriggerCandidate,
  ): string | { [key: string]: any } => {
    const integrationId: string | undefined =
      trigger?.triggerReference?.integrationId;

    if (R.isTruthy(integrationId) && isSmartSuiteIntegration(integrationId)) {
      return smartsuiteFormatter(
        trigger,
        integrations,
        applications,
        members,
        teams,
      );
    }
    return otherFormatter(trigger, integrations, applications, members, teams);
  };

  const getActionsText = (
    automation: AutomationCandidate,
    members: Member[],
    teams: Team[],
    /* eslint-disable  @typescript-eslint/no-explicit-any */
  ): string | { [key: string]: any } => {
    const actions: ActionCandidate[] = automation.actionGroups.flatMap(
      (actionGroup) =>
        actionGroup.config.oneofKind === "actions"
          ? actionGroup.config.actions.actions
          : [],
    );

    const hasLooping = automation.actionGroups.find(
      (actionGroup) => actionGroup.config.oneofKind === "loop",
    );

    const hasBranches = automation.actionGroups.find(
      (actionGroup) => actionGroup.config.oneofKind === "branches",
    );

    const trigger: TriggerCandidate = automation.trigger;
    const outputList: OutputDescription[] = actions
      .flatMap((action) => getOutputDescriptions(action))
      .concat(getOutputDescriptions(trigger));

    const actionsText = actions
      .map((action, index) => {
        const integrationId = action?.actionReference?.integrationId;
        let formatFn: ActionsFormatter = otherActionFormatter;
        if (
          R.isTruthy(integrationId) &&
          isSmartSuiteIntegration(integrationId)
        ) {
          formatFn = smartsuiteActionFormatter;
        }
        return index === 0
          ? formatFn(
              action,
              applications,
              outputList,
              members,
              teams,
              integrations,
            )
          : {
              key: "phrase-builder.then-action-text",
              data: {
                actionText: formatFn(
                  action,
                  applications,
                  outputList,
                  members,
                  teams,
                  integrations,
                ),
              },
            };
      })
      .filter((action) => R.isTruthy(action));

    if (hasLooping) {
      actionsText.push({
        key: actions.length
          ? "phrase-builder.loop-action-text"
          : "phrase-builder.loop-action-tex-without-arrow",
      });
    }

    if (hasBranches) {
      actionsText.push({
        key: actionsText.length
          ? "phrase-builder.branches-action-text"
          : "phrase-builder.branches-action-text-without-arrow",
      });
    }

    return actionsText;
  };
  const text = {
    key: "phrase-builder.when-trigger-actions",
    data: {
      trigger: getTriggerText(automation.trigger),
      actions: getActionsText(automation, members, teams),
    },
  };
  return JSON.stringify(text);
};
